import {
  DEFAULT_MOBILE_APP_CONFIG,
  defaultSubscriptionConfig,
  type RemoteConfig,
  type UpdateSettingsRequest,
} from './mobileConfigTypes';
import { type SubscriptionConfig, type SubscriptionTier } from './subscriptionTypes';

const SUBSCRIPTION_TIERS: SubscriptionTier[] = ['free', 'silver', 'gold', 'platinum'];

type RemoteConfigInput = Omit<Partial<RemoteConfig>, 'maintenance' | 'forceUpdate' | 'imagePickerConfig'> & {
  maintenance?: Partial<RemoteConfig['maintenance']>;
  forceUpdate?: Omit<Partial<RemoteConfig['forceUpdate']>, 'storeUrl'> & {
    storeUrl?: Partial<RemoteConfig['forceUpdate']['storeUrl']>;
  };
  imagePickerConfig?: Partial<RemoteConfig['imagePickerConfig']>;
};

type SubscriptionConfigInput = Omit<Partial<SubscriptionConfig>, 'tiers'> & {
  tiers?: Partial<{
    [Tier in SubscriptionTier]: Partial<SubscriptionConfig['tiers'][Tier]>;
  }>;
};

export const mergeRemoteConfig = (remote: RemoteConfigInput | undefined): RemoteConfig => ({
  ...DEFAULT_MOBILE_APP_CONFIG.REMOTE_CONFIG,
  ...remote,
  maintenance: {
    ...DEFAULT_MOBILE_APP_CONFIG.REMOTE_CONFIG.maintenance,
    ...remote?.maintenance,
  },
  forceUpdate: {
    ...DEFAULT_MOBILE_APP_CONFIG.REMOTE_CONFIG.forceUpdate,
    ...remote?.forceUpdate,
    storeUrl: {
      ...DEFAULT_MOBILE_APP_CONFIG.REMOTE_CONFIG.forceUpdate.storeUrl,
      ...remote?.forceUpdate?.storeUrl,
    },
  },
  imagePickerConfig: {
    ...DEFAULT_MOBILE_APP_CONFIG.REMOTE_CONFIG.imagePickerConfig,
    ...remote?.imagePickerConfig,
  },
  featureFlags: {
    ...DEFAULT_MOBILE_APP_CONFIG.REMOTE_CONFIG.featureFlags,
    ...remote?.featureFlags,
  },
  customMessages: {
    ...DEFAULT_MOBILE_APP_CONFIG.REMOTE_CONFIG.customMessages,
    ...remote?.customMessages,
  },
});

export const mergeSubscriptionConfig = (remote: SubscriptionConfigInput | undefined): SubscriptionConfig => {
  const tiers = { ...defaultSubscriptionConfig.tiers };

  for (const tier of SUBSCRIPTION_TIERS) {
    tiers[tier] = {
      ...defaultSubscriptionConfig.tiers[tier],
      ...remote?.tiers?.[tier],
    };
  }

  return {
    ...defaultSubscriptionConfig,
    ...remote,
    tiers,
  };
};

export const createMobileConfigUpdate = (
  remoteConfig: RemoteConfig,
  subscriptionConfig: SubscriptionConfig,
): UpdateSettingsRequest => ({
  updates: {
    REMOTE_CONFIG: remoteConfig,
    SUBSCRIPTIONS_CONFIG: subscriptionConfig,
  },
});
